/**
 * Single source of truth for translating a notification document into the
 * route the app should navigate to when the user clicks it.
 *
 * Why this exists: notifications written by old code paths carry routes
 * that are now broken in three ways:
 *
 *   1. Absolute URLs (`https://dancerealmx.com/...`). The wouter router
 *      treats these as relative paths and produces `/https:/.../`, which
 *      404s.
 *   2. Stale relative paths whose route was removed during refactors:
 *      `/orders/<id>`, `/dashboard/purchases`, `/dashboard/notifications`,
 *      `/seller-panel/sales`, `/control-panel/...`.
 *   3. Mismatched type/data shapes where the destination has to be derived
 *      from `notification.data` instead of `actionUrl`.
 *
 * Every dropdown / notification list MUST go through this resolver. Adding
 * new notification types: update KNOWN_TYPES below; do not branch in the
 * UI layer.
 */

export type NotificationLike = {
  type?: string | null;
  actionUrl?: string | null;
  data?: Record<string, unknown> | null;
};

const APP_ORIGINS = [
  "https://dancerealmx.com",
  "https://www.dancerealmx.com",
  "https://dancerealmx-b4a0a.web.app",
  "https://dancerealmx-b4a0a.firebaseapp.com",
];

const stripAppOrigin = (url: string): string => {
  for (const origin of APP_ORIGINS) {
    if (url.startsWith(origin)) {
      const rest = url.slice(origin.length);
      return rest.startsWith("/") ? rest : `/${rest}`;
    }
  }
  return url;
};

// Patterns whose old form no longer maps to a registered route.
// Entry: [matcher, rewriter]. Matcher is checked against the *path* only;
// query string and hash are preserved.
type Rewriter = (path: string, search: string, hash: string) => string | null;

const PATH_REWRITES: Array<[RegExp, Rewriter]> = [
  // `/orders/<orderId>` → `/orders` (only `/orders` is registered)
  [/^\/orders\/[^/?#]+$/, (_p, s, h) => `/orders${s}${h}`],
  // `/dashboard/purchases` → `/orders`
  [/^\/dashboard\/purchases$/, (_p, s, h) => `/orders${s}${h}`],
  // `/dashboard/notifications` → `/notifications`
  [/^\/dashboard\/notifications$/, (_p, s, h) => `/notifications${s}${h}`],
  // `/dashboard/profile` → `/profile` (preserve query — subscription tab uses it)
  [/^\/dashboard\/profile$/, (_p, s, h) => `/profile${s}${h}`],
  // `/seller-panel/sales` → `/seller/payments`
  [/^\/seller-panel\/sales$/, (_p, s, h) => `/seller/payments${s}${h}`],
  // `/seller-panel(?:/.*)?` → `/seller`
  [/^\/seller-panel(\/.*)?$/, (path, s, h) => `/seller${path.slice("/seller-panel".length)}${s}${h}`],
  // `/control-panel/courses/<id>/students` → `/courses/<id>` (closest live route)
  [
    /^\/control-panel\/courses\/([^/?#]+)\/students$/,
    (path, s, h) => {
      const m = path.match(/^\/control-panel\/courses\/([^/?#]+)\/students$/);
      return m ? `/courses/${m[1]}${s}${h}` : null;
    },
  ],
  // Bare `/control-panel(/...)` → `/instructor` (closest live admin surface)
  [/^\/control-panel(\/.*)?$/, (path, s, h) => `/instructor${path.slice("/control-panel".length)}${s}${h}`],
];

const splitUrl = (url: string): { path: string; search: string; hash: string } => {
  let rest = url;
  let hash = "";
  const hashIdx = rest.indexOf("#");
  if (hashIdx >= 0) {
    hash = rest.slice(hashIdx);
    rest = rest.slice(0, hashIdx);
  }
  let search = "";
  const qIdx = rest.indexOf("?");
  if (qIdx >= 0) {
    search = rest.slice(qIdx);
    rest = rest.slice(0, qIdx);
  }
  return { path: rest, search, hash };
};

export const sanitizeActionUrl = (raw: string | undefined | null): string | null => {
  if (!raw || typeof raw !== "string") return null;
  let url = raw.trim();
  if (!url) return null;

  // Absolute URL: if it points at one of our origins, drop the origin and
  // keep the path. If it points elsewhere (external link), leave it for the
  // caller — they'll use window.location.assign for off-app navigation.
  if (/^https?:\/\//i.test(url)) {
    const stripped = stripAppOrigin(url);
    if (stripped === url) {
      // External — caller handles full URL navigation.
      return url;
    }
    url = stripped;
  }

  if (!url.startsWith("/")) {
    url = `/${url}`;
  }

  const { path, search, hash } = splitUrl(url);

  for (const [matcher, rewriter] of PATH_REWRITES) {
    if (matcher.test(path)) {
      const rewritten = rewriter(path, search, hash);
      if (rewritten) return rewritten;
    }
  }

  return `${path}${search}${hash}`;
};

export const isExternalUrl = (url: string | null | undefined): boolean => {
  if (!url || typeof url !== "string") return false;
  if (!/^https?:\/\//i.test(url)) return false;
  return !APP_ORIGINS.some((o) => url.startsWith(o));
};

const buildQuery = (params: Record<string, string | null | undefined>): string => {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Resolve the destination route for a notification. Returns either an
 * in-app path (starts with `/`) or an absolute external URL (starts with
 * `http`). Callers should branch on `isExternalUrl(result)` to decide
 * between `setLocation(result)` and `window.location.assign(result)`.
 *
 * Falls back to `/notifications` (which IS a registered route) instead of
 * the legacy `/dashboard/notifications` which is not registered.
 */
export const resolveNotificationRoute = (notification: NotificationLike): string => {
  const type = String(notification.type || "").toLowerCase();
  const data = (notification.data || {}) as Record<string, unknown>;
  // Some senders put the destination at the top level and others inside
  // `data` -- the payout notifier writes data.actionUrl. Only the top-level
  // field was read, so every payout notification fell through to the default
  // and resolved to /notifications: clicking one took the seller to the page
  // they were already looking at. Same dead-end already fixed for favourites.
  const dataActionUrl = typeof data.actionUrl === "string" ? data.actionUrl : undefined;
  const sanitized = sanitizeActionUrl(notification.actionUrl ?? dataActionUrl);

  switch (type) {
    case "jobapplication":
    case "job_application":
      return data.jobId ? `/jobs/${data.jobId}/applicants` : "/messages";

    case "jobacceptance":
    case "job_acceptance":
    case "jobrejection":
    case "job_rejection": {
      // Open the actual conversation thread (where the hiring team's response
      // now lives) — NOT the job description, and NOT an empty inbox.
      // `?conversation=` is what the messages page reads; fall back to `?job=`
      // (also handled now) and then the inbox for legacy notifications.
      const convId = typeof data.conversationId === "string" ? data.conversationId : "";
      if (convId) return `/messages?conversation=${convId}`;
      return data.jobId ? `/messages?job=${data.jobId}` : "/messages";
    }

    case "job_posted_matching_skills":
    case "jobposted":
    case "job_posted":
      return data.jobId ? `/jobs/${data.jobId}/view` : "/jobs";

    case "connectrequest":
    case "connect_request":
    case "connectconfirmed":
    case "connect_confirmed":
    case "connectcancelled":
    case "connect_cancelled":
      return "/professional-panel";

    case "message": {
      // Open the specific thread, not the auto-selected first conversation
      // ("redirecting to the wrong messages"). Prefer conversationId; the
      // messages page also resolves ?job=.
      const convId = typeof data.conversationId === "string" ? data.conversationId : "";
      if (convId) return `/messages?conversation=${convId}`;
      const jobId = typeof data.jobId === "string" ? data.jobId : "";
      return jobId ? `/messages?job=${jobId}` : "/messages";
    }

    case "profileview":
    case "profile_view":
      return "/profile";

    case "purchaseconfirmation":
    case "purchase_confirmation": {
      // Order-level confirmations route to /orders (every line item with
      // download buttons). Per-item confirmations route to the resource
      // detail page. Prefer the (sanitized) actionUrl since the backend
      // already encodes that distinction; fall back to data hints.
      if (sanitized) return sanitized;
      const itemId = typeof data.itemId === "string" ? data.itemId : null;
      const itemType = typeof data.itemType === "string" ? data.itemType.toLowerCase() : "";
      if (itemId && (itemType === "resource" || itemType === "curriculum")) {
        return `/curriculum/${itemId}`;
      }
      return "/orders";
    }

    case "salenotification":
    case "sale_notification":
    case "curriculumsold": {
      const purchaseId = typeof data.purchaseId === "string" ? data.purchaseId : null;
      const resourceId = typeof data.resourceId === "string" ? data.resourceId : null;
      const qs = buildQuery({ highlight: purchaseId, resource: resourceId });
      return `/seller/payments${qs}`;
    }

    // Payout events all concern the seller's money, and the Payments page is
    // where it lives -- including the "Manage on Stripe" button, which is the
    // only way to reach Stripe. A Stripe URL cannot be linked directly from a
    // notification: Express dashboard links are single-use and expire within
    // minutes, so one baked in here would be dead before it was clicked. It
    // has to be minted server-side at the moment the seller asks for it.
    case "payout":
      return "/seller/payments";

    // notifyOnProfessionalFavorite writes type 'PROFILE_FAVORITED' → lowercased
    // here to 'profile_favorited'. Without this case it fell through to the
    // default and dead-ended on /notifications (reported: like-notifications
    // "don't take me anywhere"). Someone favorited YOUR profile → show who.
    case "professional_favorite":
    case "professionalfavorite":
    case "profile_favorited": {
      const actorUid = typeof data.actorUid === "string" ? data.actorUid : "";
      return actorUid ? `/instructors/${actorUid}` : "/dashboard";
    }

    // notifyOnResourceFavorite writes type 'RESOURCE_FAVORITED' → lowercased to
    // 'resource_favorited'. Route to the favorited item (resource/job/course).
    case "resource_favorited": {
      const t = typeof data.targetType === "string" ? data.targetType.toLowerCase() : "";
      const id =
        (typeof data.targetId === "string" && data.targetId) ||
        (typeof data.itemId === "string" && data.itemId) ||
        "";
      if (!id) return "/notifications";
      if (t === "job") return `/jobs/${id}/view`;
      if (t === "course") return `/courses/${id}`;
      return `/curriculum/${id}`;
    }

    case "subscriptionactivated":
    case "subscription_activated":
    case "subscriptioncancelled":
    case "subscription_cancelled":
    case "subscriptionresumed":
    case "subscription_resumed":
    case "subscriptionpastdue":
    case "subscription_past_due":
    case "subscriptionrenewed":
    case "subscription_renewed":
    case "subscriptiontrialending":
    case "subscription_trial_ending":
      return sanitized || "/subscription";

    case "courseenrollment":
    case "course_enrollment":
      if (sanitized) return sanitized;
      return data.courseId ? `/courses/${data.courseId}` : "/dashboard";

    case "studentenrolled":
    case "student_enrolled":
      if (sanitized) return sanitized;
      // /courses/:id/students never existed; the instructor's student list does.
      return "/instructor/students";

    // An instructor's course sale (lifecycle trigger writes no link): it used
    // to fall to the default and dead-end on /notifications.
    case "coursesold":
    case "course_sold":
      return sanitized || "/instructor/earnings?tab=sales&type=course";

    // A learner submitted a written / video assessment: the instructor marks it.
    case "assessment_submitted":
      return sanitized || (data.courseId ? `/instructor/assessments?course=${encodeURIComponent(String(data.courseId))}` : "/instructor/assessments");

    // The learner's assessment has its mark: the result and feedback are on the quiz.
    case "assessment_marked":
      return sanitized || (data.courseId ? `/courses/${data.courseId}/view` : "/my-courses");

    case "certificate":
    case "course_completed":
    case "coursecompleted":
      return sanitized || "/certificates";

    case "payment":
    default:
      return sanitized || "/notifications";
  }
};
